import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "./lib/auth";
import { ThemeProvider, useTheme } from "./lib/theme";
import { Toaster } from "sonner";
import Landing from "./pages/Landing";
import Marketplace from "./pages/Marketplace";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AppShell from "./pages/AppShell";
import Dashboard from "./pages/Dashboard";
import Services from "./pages/Services";
import Expenses from "./pages/Expenses";
import Calendar from "./pages/Calendar";
import Team from "./pages/Team";
import Settings from "./pages/Settings";
import ColaboradorSettings from "./pages/ColaboradorSettings";
import MyMonthlyInvoice from "./pages/MyMonthlyInvoice";
import Payroll from "./pages/Payroll";
import Catalog from "./pages/Catalog";
import Providers from "./pages/Providers";
import Tax from "./pages/Tax";
import Billing from "./pages/Billing";
import RecurringInvoices from "./pages/RecurringInvoices";
import WelcomeSplash, { shouldShowSplash, getRolePreference } from "./pages/WelcomeSplash";
import Pricing from "./pages/Pricing";
import NotFound from "./pages/NotFound";
import BillingSuccess from "./pages/BillingSuccess";
import BillingCancel from "./pages/BillingCancel";
import Vouchers from "./pages/Vouchers";
import ColaboradorHome from "./pages/ColaboradorHome";
import GestorHome from "./pages/GestorHome";
import EmpleadoHome from "./pages/empleado/EmpleadoHome";
import EmpleadoAgenda from "./pages/empleado/EmpleadoAgenda";
import EmpleadoClientes from "./pages/empleado/EmpleadoClientes";
import EmpleadoMyProfile from "./pages/empleado/EmpleadoMyProfile";
import Availability from "./pages/Availability";
import Customers from "./pages/Customers";
import PublicBooking from "./pages/PublicBooking";
import CentroLanding from "./pages/CentroLanding";
import CityLanding from "./pages/CityLanding";
import AddBusiness from "./pages/AddBusiness";
import ConfirmSubmission from "./pages/ConfirmSubmission";
import ClaimBusiness from "./pages/ClaimBusiness";
import ConfirmClaim from "./pages/ConfirmClaim";
import LeadsInbox from "./pages/LeadsInbox";
import Onboarding from "./pages/Onboarding";
import TitularFirstRun from "./pages/titular/TitularFirstRun";
import ColaboradorOnboarding from "./pages/onboarding/ColaboradorOnboarding";
import GestorOnboarding from "./pages/onboarding/GestorOnboarding";
import EmpleadoOnboarding from "./pages/onboarding/EmpleadoOnboarding";
import ClienteOnboarding from "./pages/onboarding/ClienteOnboarding";
import AdminAnalytics from "./pages/AdminAnalytics";
import { Privacy, Terms } from "./pages/Legal";
import Help from "./pages/Help";
import ReferralLanding from "./pages/ReferralLanding";
import { NpsModal } from "./components/NpsModal";
import AppointmentToken from "./pages/AppointmentToken";
import SuperAdmin from "./pages/SuperAdmin";
import FounderShell from "./pages/FounderShell";
import FounderAdvisorDashboard from "./pages/advisor/FounderAdvisorDashboard";
import ClientAdvisorDashboard from "./pages/advisor/ClientAdvisorDashboard";
import DashboardSection from "./pages/founderOS/sections/DashboardSection";
import IntelligenceSection from "./pages/founderOS/sections/IntelligenceSection";
import GrowthSection from "./pages/founderOS/sections/GrowthSection";
import OperationsSection from "./pages/founderOS/sections/OperationsSection";
import AgentsSection from "./pages/founderOS/sections/AgentsSection";
import SecuritySection from "./pages/founderOS/sections/SecuritySection";
import AutomationSection from "./pages/founderOS/sections/AutomationSection";
import NotificationsSection from "./pages/founderOS/sections/NotificationsSection";
// Fase 8 · Convergencia completada · los componentes legacy FO standalone (FounderHome,
// AuditCenter, KnowledgeBase, AgentCenter, ContentStudio, ExecutiveReports,
// SecurityGovernance) se siguen utilizando desde su `Section` correspondiente.
// Las rutas /founder/{home,audit-center,knowledge-base,agent-center,content-studio,
// executive-reports,security-governance,reports,audits,knowledge,content} ahora
// son REDIRECTS hacia su equivalente canonical V2 (ver bloque /founder más abajo).
import OrganizationChats from "./pages/OrganizationChats";
import CustomerLogin from "./pages/CustomerLogin";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import CustomerSignup from "./pages/CustomerSignup";
import CustomerAccount from "./pages/CustomerAccount";
import { CustomerAuthProvider } from "./lib/customerAuth";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { PwaUpdateToast } from "./components/PwaUpdateToast";
import { FloatingChatWidget } from "./components/FloatingChatWidget";
import StickyMobileActions from "./components/layout/StickyMobileActions";
import "./App.css";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center text-sm text-[var(--ag-text-dim)]">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function HomeRouter() {
  const { user, loading } = useAuth();
  // Esperar a que la sesión esté resuelta antes de decidir destino.
  if (loading) return <div className="h-screen flex items-center justify-center text-sm text-[var(--ag-text-dim)]">Cargando…</div>;
  // Convergence Sprint Feb 2026: superadmin entra al Founder Control Center v2
  if (user?.role === "superadmin") return <Navigate to="/founder" replace />;
  if (user?.role === "colaborador") return <ColaboradorHome />;
  if (user?.role === "gestor") return <GestorHome />;
  // Fase 2A · P0-1 · rol empleado tiene home propio (sin datos financieros)
  if (user?.role === "empleado") return <EmpleadoHome />;
  return <Dashboard />;
}

// PWA · al cargar `/` sin sesión, redirigimos según preferencia guardada.
// - Sin preferencia → WelcomeSplash (primera vez).
// - "client" → /marketplace.
// - "pro" → /login (también podrá seguir a /register desde allí).
// - Si el usuario quiere ver la landing larga, accede manualmente a /home o /landing.
function PublicRootGate() {
  if (shouldShowSplash()) return <WelcomeSplash />;
  const role = getRolePreference();
  if (role === "client") return <Navigate to="/marketplace" replace />;
  if (role === "pro") return <Navigate to="/login" replace />;
  return <Landing />;
}

function RootRouter() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/landing" element={<Landing />} />
      <Route path="/home" element={<Landing />} />
      <Route path="/welcome" element={<WelcomeSplash />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/precios" element={<Pricing />} />
      <Route path="/privacidad" element={<Privacy />} />
      <Route path="/terminos" element={<Terms />} />
      <Route path="/marketplace" element={<Marketplace />} />
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/register" element={user ? <Navigate to="/" replace /> : <Register />} />
      <Route path="/reservar/:slug" element={<PublicBooking />} />
      <Route path="/centro/:slug" element={<CentroLanding />} />
      <Route path="/ciudad/:city" element={<CityLanding />} />
      <Route path="/ciudad/:city/:categoria" element={<CityLanding />} />
      {/* Fase 17 · Crecimiento orgánico */}
      <Route path="/anadir-negocio" element={<AddBusiness />} />
      <Route path="/anadir-negocio/confirmar" element={<ConfirmSubmission />} />
      {/* Sprint D2 · Reclamación */}
      <Route path="/reclamar/:slug" element={<ClaimBusiness />} />
      <Route path="/reclamar/confirmar" element={<ConfirmClaim />} />
      <Route path="/ayuda" element={<Help />} />
      <Route path="/r/:code" element={<ReferralLanding />} />
      <Route path="/cita/:token" element={<AppointmentToken />} />
      <Route path="/cita/:token/confirmar" element={<AppointmentToken />} />
      <Route path="/cita/:token/cancelar" element={<AppointmentToken />} />
      {/* Fase 2A · P0-3 · Onboarding específico por rol (public routes, accept ?token=...) */}
      <Route path="/colaborador/onboarding" element={<ColaboradorOnboarding />} />
      <Route path="/gestor/onboarding" element={<GestorOnboarding />} />
      <Route path="/empleado/onboarding" element={<EmpleadoOnboarding />} />
      <Route path="/cliente/onboarding" element={<ClienteOnboarding />} />
      {/* Fase 2B · B1 · Titular First Run (privado admin) */}
      <Route path="/titular/first-run" element={<Protected roles={["admin"]}><TitularFirstRun /></Protected>} />
      {/* Sprint F · Cliente Final */}
      <Route path="/cliente/login" element={<CustomerLogin />} />
      <Route path="/cliente/signup" element={<CustomerSignup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/cliente/recuperar-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/mi-cuenta" element={<CustomerAccount />} />
      <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
      <Route path="/billing/cancel" element={<BillingCancel />} />
      <Route path="/admin" element={<Protected roles={["superadmin"]}><SuperAdmin showLegacyBanner /></Protected>} />
      {/* Sprint T-13 · Advisor Dashboard · founder + client (cliente-as-viewer · superadmin only en T-13 · P1 Security relajará gates de routers AG-01..AG-05) */}
      <Route path="/founder/advisor" element={<Protected roles={["superadmin"]}><FounderAdvisorDashboard /></Protected>} />
      <Route path="/centro/advisor" element={<Protected roles={["superadmin","admin"]}><ClientAdvisorDashboard /></Protected>} />
      <Route path="/centro/advisor/:slug" element={<Protected roles={["superadmin","admin"]}><ClientAdvisorDashboard /></Protected>} />
      {/* Founder Control Center v2 · Convergence Sprint Q · Feb 2026 · ÚNICO punto de entrada del founder */}
      <Route path="/founder" element={<Protected roles={["superadmin"]}><FounderShell /></Protected>}>
        <Route index element={<DashboardSection />} />
        <Route path="dashboard" element={<DashboardSection />} />
        <Route path="intelligence" element={<IntelligenceSection />} />
        <Route path="growth" element={<GrowthSection />} />
        <Route path="operations" element={<OperationsSection />} />
        <Route path="agents" element={<AgentsSection />} />
        <Route path="security" element={<SecuritySection />} />
        <Route path="automation" element={<AutomationSection />} />
        <Route path="notifications" element={<NotificationsSection />} />
        {/* Fase 8 · Convergencia · Redirects legacy v1 → canonical V2 (compat 100%) */}
        <Route path="home"                 element={<Navigate to="/founder/dashboard" replace />} />
        <Route path="reports"              element={<Navigate to="/founder/dashboard" replace />} />
        <Route path="executive-reports"    element={<Navigate to="/founder/dashboard" replace />} />
        <Route path="audits"               element={<Navigate to="/founder/intelligence?sub=auditorias" replace />} />
        <Route path="audit-center"         element={<Navigate to="/founder/intelligence?sub=auditorias" replace />} />
        <Route path="knowledge"            element={<Navigate to="/founder/intelligence?sub=knowledge" replace />} />
        <Route path="knowledge-base"       element={<Navigate to="/founder/intelligence?sub=knowledge" replace />} />
        <Route path="content"              element={<Navigate to="/founder/growth?sub=content" replace />} />
        <Route path="content-studio"       element={<Navigate to="/founder/growth?sub=content" replace />} />
        <Route path="agent-center"         element={<Navigate to="/founder/agents" replace />} />
        <Route path="security-governance"  element={<Navigate to="/founder/security?sub=checklist" replace />} />
      </Route>
      <Route path="/" element={user ? <Protected><AppShell /></Protected> : <PublicRootGate />}>
        <Route index element={<HomeRouter />} />
        <Route path="services" element={<Protected roles={["admin","colaborador"]}><Services /></Protected>} />
        <Route path="customers" element={<Protected roles={["admin","gestor","colaborador"]}><Customers /></Protected>} />
        <Route path="expenses" element={<Protected roles={["admin","gestor","colaborador"]}><Expenses /></Protected>} />
        <Route path="calendar" element={<Protected roles={["admin","colaborador"]}><Calendar /></Protected>} />
        <Route path="chats" element={<Protected roles={["admin","colaborador","empleado"]}><OrganizationChats /></Protected>} />
        <Route path="leads" element={<Protected roles={["admin","gestor"]}><LeadsInbox /></Protected>} />
        <Route path="onboarding" element={<Protected roles={["admin"]}><Onboarding /></Protected>} />
        <Route path="analytics" element={<Protected roles={["admin","gestor"]}><AdminAnalytics /></Protected>} />
        <Route path="team" element={<Protected roles={["admin"]}><Team /></Protected>} />
        <Route path="payroll" element={<Protected roles={["admin"]}><Payroll /></Protected>} />
        <Route path="catalog" element={<Protected roles={["admin"]}><Catalog /></Protected>} />
        <Route path="providers" element={<Protected roles={["admin","gestor"]}><Providers /></Protected>} />
        <Route path="tax" element={<Protected roles={["admin","gestor"]}><Tax /></Protected>} />
        <Route path="availability" element={<Protected roles={["admin","colaborador"]}><Availability /></Protected>} />
        <Route path="settings" element={<Protected roles={["admin"]}><Settings /></Protected>} />
        <Route path="my-settings" element={<Protected roles={["colaborador","gestor"]}><ColaboradorSettings /></Protected>} />
        <Route path="my-invoice" element={<Protected roles={["colaborador"]}><MyMonthlyInvoice /></Protected>} />
        <Route path="billing" element={<Protected roles={["admin"]}><Billing /></Protected>} />
        <Route path="recurring-invoices" element={<Protected roles={["admin","owner"]}><RecurringInvoices /></Protected>} />
        <Route path="vouchers" element={<Protected roles={["admin"]}><Vouchers /></Protected>} />
        {/* Fase 2A · P0-1 · Portal Empleado (sin acceso financiero) */}
        <Route path="empleado/agenda" element={<Protected roles={["empleado"]}><EmpleadoAgenda /></Protected>} />
        <Route path="empleado/clientes" element={<Protected roles={["empleado"]}><EmpleadoClientes /></Protected>} />
        <Route path="empleado/mi-perfil" element={<Protected roles={["empleado"]}><EmpleadoMyProfile /></Protected>} />
      </Route>
      {/* Sprint P.0 · 404 pública (sustituye el soft-404 anterior a `/`) */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function NpsGate() {
  const { user } = useAuth();
  // Solo intentar mostrar a admin/gestor (el backend valida también).
  if (!user || !["admin", "gestor"].includes(user.role)) return null;
  return <NpsModal />;
}

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster position="top-right" richColors theme={theme} />;
}

export default function App() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });
  return (
    <div className="App">
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <CustomerAuthProvider>
              <BrowserRouter>
                <RootRouter />
                <NpsGate />
                <ThemedToaster />
                <PWAInstallPrompt />
                <PwaUpdateToast />
                <FloatingChatWidget />
                <StickyMobileActions />
              </BrowserRouter>
            </CustomerAuthProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </div>
  );
}
